import React, { useEffect, useState } from "react";
import Modal from "@mui/material/Modal";
import Box from "@mui/material/Box";
import Typography from "@mui/material/Typography";
import IconButton from "@mui/material/IconButton";
import CloseIcon from "@mui/icons-material/Close";
import Button from "@mui/material/Button";
import Banner from "../Components/Shared/Layout/Banner";
import IntroBlock from "../Components/Shared/Layout/IntroBlock";
import withLanguage from "../HOC/withLanguage";
import {useSelector} from "react-redux";

function EmployeeDual({ language, endpoint, truncateAt = 100 }) {
    const [items, setItems] = useState([]);
    const [loading, setLoading] = useState(true);
    const [err, setErr] = useState(null);
    const [openIndex, setOpenIndex] = useState(null);
    const lang = useSelector(state => state.language.currentLanguage)

    useEffect(() => {
        const ac = new AbortController();
        setLoading(true);
        setErr(null);

        const url = `/api/dual_system/dual_system?lang=${encodeURIComponent(lang)}`;
        fetch(url, { signal: ac.signal })
            .then(async (r) => {
                if (!r.ok) throw new Error(`HTTP ${r.status}`);
                return r.json();
            })
            .then((data) => {
                const list = Array.isArray(data?.records) ? data.records : [];
                // Only show up to 3 as requested
                setItems(list.slice(0, 3));
            })
            .catch((e) => {
                if (e.name !== "AbortError") setErr(e.message || "Failed to load");
            })
            .finally(() => setLoading(false));

        return () => ac.abort();
    }, [endpoint, lang]);

    const truncate = (str, max = truncateAt) =>
        typeof str === "string" && str.length > max ? str.slice(0, max) + "..." : str ?? "";

    return (
        <>
            <Banner img="/CareerAdvice/banner.jpg" title={language.banner} />
            <IntroBlock>{language.dual_intro}</IntroBlock>

            {loading && <div className="p-6 text-center">Loading…</div>}
            {err && <div className="p-6 text-center text-red-600">Error: {err}</div>}

            {!loading && !err && (
                <div className="grid grid-cols-3 max-md:grid-cols-1 gap-6 mt-6 m-5 xl:mx-[15rem] lg:mx-[7rem] md:mx-[3rem] xs:mx-[2.5rem]">
                    {items.map((item, i) => {
                        const needsMore = (item?.description?.length || 0) > truncateAt;
                        return (
                            <div key={item.id ?? i} className="space-y-2 text-center">
                                {/* top red title (kept as-is from your design) */}
                                <h3 className="text-red-600 font-bold">
                                    {item?.title || ""}
                                </h3>

                                <div className="rounded-xl shadow-md bg-white p-6 text-left">
                                    {/* inner red subtitle (maps to your cardTitle place) */}
                                    {item?.subtitle && (
                                        <h4 className="text-red-600 font-semibold">{item.subtitle}</h4>
                                    )}

                                    {/* description with modal-based Read more */}
                                    {item?.description && (
                                        <p className="text-[14px] text-gray-700 mt-2">
                                            {truncate(item.description, truncateAt)}{" "}
                                            {needsMore && (
                                                <button
                                                    onClick={() => setOpenIndex(i)}
                                                    className="text-blue-600 cursor-pointer select-none underline"
                                                    aria-haspopup="dialog"
                                                    aria-controls={`dual-modal-${i}`}
                                                >
                                                    {language["read_more"] || "Read more"}
                                                </button>
                                            )}
                                        </p>
                                    )}
                                </div>
                            </div>
                        );
                    })}
                </div>
            )}

            {/* Modal with full description (Read more logic) */}
            <Modal
                open={openIndex !== null}
                onClose={() => setOpenIndex(null)}
                aria-labelledby={`dual-modal-title-${openIndex ?? 0}`}
                aria-describedby={`dual-modal-desc-${openIndex ?? 0}`}
            >
                <Box
                    id={`dual-modal-${openIndex ?? 0}`}
                    sx={{
                        position: "absolute",
                        top: "50%",
                        left: "50%",
                        transform: "translate(-50%, -50%)",
                        width: { xs: "90%", sm: 600 },
                        bgcolor: "background.paper",
                        borderRadius: 2,
                        boxShadow: 24,
                        p: 3,
                    }}
                >
                    <div className="flex items-center justify-between">
                        <Typography id={`dual-modal-title-${openIndex ?? 0}`} variant="h6" component="h3">
                            {items[openIndex ?? 0]?.title || ""}
                        </Typography>
                        <IconButton aria-label={language["close"] || "Close"} onClick={() => setOpenIndex(null)}>
                            <CloseIcon />
                        </IconButton>
                    </div>

                    <Typography
                        id={`dual-modal-desc-${openIndex ?? 0}`}
                        sx={{ mt: 2, whiteSpace: "pre-wrap" }}
                    >
                        {items[openIndex ?? 0]?.description || ""}
                    </Typography>

                    <div className="flex justify-end mt-4">
                        <Button variant="contained" onClick={() => setOpenIndex(null)}>
                            {language["close"] || "Close"}
                        </Button>
                    </div>
                </Box>
            </Modal>
        </>
    );
}

export default withLanguage(EmployeeDual, "EmployeeDual");
